import { useCallback, useRef, useState } from "react";
import { coreGet } from "@/services/coreApi";
import { useRouteStore } from "@/features/route/state/RouteStore.jsx";
import { normalizeTreeMetadata } from "@/features/route/state/RouteAdapters.jsx";
import {
  selectRoutesForVoyage,
  selectVoyagesForVessel,
} from "@/features/route/state/RouteSelectors.jsx";

// Initial voyage window and the increment added each time the user loads older
// voyages (months back from now).
export const VOYAGE_MONTHS_STEP = 3;

/**
 * useRouteTreeData
 *
 * Purpose:
 * - Load voyages/routes into the RouteStore (the single entity truth) and
 *   own the fetch bookkeeping (loading/error/pagination). Vessels come from
 *   useVesselsLoader, mounted once in MainComponent.
 * - Expose void mutation helpers that dispatch store actions.
 */
export default function useRouteTreeData({
  accessContext,
  setAccessContext,
}) {
  const {
    state: storeState,
    setVoyagesForVessel: storeSetVoyagesForVessel,
    setRoutesForVoyage: storeSetRoutesForVoyage,
    upsertEntities: storeUpsertEntities,
    removeRoute: storeRemoveRoute,
    removeVoyage: storeRemoveVoyage,
    archiveRouteSiblings: storeArchiveRouteSiblings,
    archiveVoyageSiblings: storeArchiveVoyageSiblings,
    archiveVoyageRoutes: storeArchiveVoyageRoutes,
  } = useRouteStore();

  // Ref so the loaders' cache-hit path reads the CURRENT store truth without
  // re-creating the callbacks on every dispatch.
  const storeStateRef = useRef(storeState);
  storeStateRef.current = storeState;

  const [voyagesLoadingByVesselId, setVoyagesLoadingByVesselId] = useState({});
  const [voyagesErrorByVesselId, setVoyagesErrorByVesselId] = useState({});
  // How many months back the voyage list has been loaded, per vessel. Grows in
  // VOYAGE_MONTHS_STEP increments when the user loads older voyages. Doubles
  // as the fetched-gate for rendering the store's voyage relations.
  const [voyageMonthsByVesselId, setVoyageMonthsByVesselId] = useState({});
  // In-flight default loads per vessel (string key, like the state maps) so
  // concurrent callers in one commit share the request instead of refetching.
  const pendingVoyageLoadsRef = useRef(new Map());

  const [routesLoadingByVoyageId, setRoutesLoadingByVoyageId] = useState({});
  const [routesErrorByVoyageId, setRoutesErrorByVoyageId] = useState({});
  const [routesFetchedByVoyageId, setRoutesFetchedByVoyageId] = useState({});

  const loadVoyagesForVessel = useCallback(
    async (vesselId, months, { forceRefresh = false } = {}) => {
      if (!vesselId) return [];

      const alreadyLoaded = voyageMonthsByVesselId[vesselId];
      // Default load: only fetch once. Explicit months (load older) or
      // forceRefresh (e.g. after a save): always refetch. Cache hits are
      // served from the store so local mutations are reflected.
      const isDefaultLoad = months == null && !forceRefresh;
      if (months == null) {
        if (!forceRefresh && alreadyLoaded != null) {
          return selectVoyagesForVessel(storeStateRef.current, vesselId);
        }
        months = alreadyLoaded ?? VOYAGE_MONTHS_STEP;
      }
      const pendingKey = String(vesselId);
      const pending = pendingVoyageLoadsRef.current.get(pendingKey);
      if (isDefaultLoad && pending) return pending;

      setVoyagesLoadingByVesselId((prev) => ({ ...prev, [vesselId]: true }));
      setVoyagesErrorByVesselId((prev) => ({ ...prev, [vesselId]: "" }));

      const load = (async () => {
        try {
          const data = await coreGet(
            `/voyages?vessel_id=${vesselId}&type=planned&months=${months}`,
            accessContext,
            setAccessContext,
            ".core /voyages error=",
          );
          const voyages = data?.voyages ?? [];
          setVoyageMonthsByVesselId((prev) => ({ ...prev, [vesselId]: months }));
          storeSetVoyagesForVessel(
            vesselId,
            normalizeTreeMetadata({ voyages, vesselId }),
          );
          return voyages;
        } catch (err) {
          setVoyagesErrorByVesselId((prev) => ({
            ...prev,
            [vesselId]: err?.message ?? "Failed to load voyages",
          }));
          return [];
        } finally {
          setVoyagesLoadingByVesselId((prev) => ({ ...prev, [vesselId]: false }));
        }
      })();
      if (isDefaultLoad) {
        pendingVoyageLoadsRef.current.set(pendingKey, load);
        load.finally(() => {
          if (pendingVoyageLoadsRef.current.get(pendingKey) === load) {
            pendingVoyageLoadsRef.current.delete(pendingKey);
          }
        });
      }
      return load;
    },
    [
      voyageMonthsByVesselId,
      accessContext,
      setAccessContext,
      storeSetVoyagesForVessel,
    ],
  );

  const loadOlderVoyagesForVessel = useCallback(
    (vesselId) => {
      const current = voyageMonthsByVesselId[vesselId] ?? VOYAGE_MONTHS_STEP;
      return loadVoyagesForVessel(vesselId, current + VOYAGE_MONTHS_STEP);
    },
    [voyageMonthsByVesselId, loadVoyagesForVessel],
  );

  const loadRoutesForVoyage = useCallback(
    async (voyageId, { forceRefresh = false } = {}) => {
      if (!voyageId) return [];
      // Serve from the store unless a refresh is forced (e.g. after a save),
      // so local mutations (activate/archive/delete) are reflected.
      if (!forceRefresh && routesFetchedByVoyageId[voyageId]) {
        return selectRoutesForVoyage(storeStateRef.current, voyageId);
      }

      setRoutesLoadingByVoyageId((prev) => ({ ...prev, [voyageId]: true }));
      setRoutesErrorByVoyageId((prev) => ({ ...prev, [voyageId]: "" }));

      try {
        const data = await coreGet(
          `/routes?voyage_id=${voyageId}&type=planned`,
          accessContext,
          setAccessContext,
          ".core /routes error=",
        );
        const routes = data?.routes ?? [];
        setRoutesFetchedByVoyageId((prev) => ({ ...prev, [voyageId]: true }));
        storeSetRoutesForVoyage(
          voyageId,
          normalizeTreeMetadata({ routes, voyageId }),
        );
        return routes;
      } catch (err) {
        setRoutesErrorByVoyageId((prev) => ({
          ...prev,
          [voyageId]: err?.message ?? "Failed to load routes",
        }));
        return [];
      } finally {
        setRoutesLoadingByVoyageId((prev) => ({ ...prev, [voyageId]: false }));
      }
    },
    [
      routesFetchedByVoyageId,
      accessContext,
      setAccessContext,
      storeSetRoutesForVoyage,
    ],
  );

  // Void mutation helpers — thin store-dispatch wrappers. Lifecycle cascades
  // run atomically in the store reducer (no stale setState returns).
  const upsertVessel = useCallback(
    (vessel) => {
      if (!vessel || !Number.isFinite(Number(vessel?.id))) return;
      storeUpsertEntities(normalizeTreeMetadata({ vessels: [vessel] }));
    },
    [storeUpsertEntities],
  );

  const upsertVoyageForVessel = useCallback(
    (vesselId, voyage) => {
      if (!Number.isFinite(Number(vesselId)) || !voyage) return;
      storeUpsertEntities(normalizeTreeMetadata({ voyages: [voyage], vesselId }));
    },
    [storeUpsertEntities],
  );

  const archiveOtherActiveVoyages = useCallback(
    (vesselId, keepVoyageId) => {
      if (!Number.isFinite(Number(vesselId))) return;
      storeArchiveVoyageSiblings(vesselId, keepVoyageId);
    },
    [storeArchiveVoyageSiblings],
  );

  const removeVoyageForVessel = useCallback(
    (vesselId, voyageId) => {
      if (!Number.isFinite(Number(voyageId))) return;
      storeRemoveVoyage(voyageId);
    },
    [storeRemoveVoyage],
  );

  const upsertRouteForVoyage = useCallback(
    (voyageId, route) => {
      if (!Number.isFinite(Number(voyageId)) || !route) return;
      storeUpsertEntities(normalizeTreeMetadata({ routes: [route], voyageId }));
    },
    [storeUpsertEntities],
  );

  const removeRouteFromVoyage = useCallback(
    (voyageId, routeId) => {
      if (!Number.isFinite(Number(routeId))) return;
      storeRemoveRoute(routeId);
    },
    [storeRemoveRoute],
  );

  const archiveOtherActivePlannedRoutes = useCallback(
    (voyageId, keepRouteId) => {
      if (!Number.isFinite(Number(voyageId))) return;
      storeArchiveRouteSiblings(voyageId, keepRouteId);
    },
    [storeArchiveRouteSiblings],
  );

  const archivePlannedRoutesForVoyage = useCallback(
    (voyageId) => {
      if (!Number.isFinite(Number(voyageId))) return;
      storeArchiveVoyageRoutes(voyageId);
    },
    [storeArchiveVoyageRoutes],
  );

  return {
    voyagesLoadingByVesselId,
    voyagesErrorByVesselId,
    voyageMonthsByVesselId,
    routesLoadingByVoyageId,
    routesErrorByVoyageId,
    routesFetchedByVoyageId,
    loadVoyagesForVessel,
    loadOlderVoyagesForVessel,
    loadRoutesForVoyage,
    upsertVessel,
    upsertVoyageForVessel,
    removeVoyageForVessel,
    archiveOtherActiveVoyages,
    upsertRouteForVoyage,
    removeRouteFromVoyage,
    archiveOtherActivePlannedRoutes,
    archivePlannedRoutesForVoyage,
  };
}
